import { useQuery, gql, useMutation } from '@apollo/client';
import {
  createCognitoUser,
  createImportedUsers,
  createUser,
  deleteCognitoUser,
  deleteUser,
  deleteUserTrigger,
  sendEmail,
  updateUser,
} from 'src/graphql/mutations';
import {
  CreateCognitoUserMutation,
  CreateCognitoUserMutationVariables,
  CreateImportedUsersMutation,
  CreateImportedUsersMutationVariables,
  CreateUserMutation,
  CreateUserMutationVariables,
  DeleteStatus,
  DeleteUserMutation,
  DeleteUserMutationVariables,
  DeleteUserTriggerMutation,
  DeleteUserTriggerMutationVariables,
  GetUserQuery,
  GetUserQueryVariables,
  ListUserByOrganizationIDQuery,
  ListUserByOrganizationIDQueryVariables,
  SendEmailMutation,
  SendEmailMutationVariables,
  UpdateUserInput,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  User,
  UserStatus,
} from 'src/API';
import { useEffect, useMemo, useState } from 'react';
import { nonNullable } from 'src/utils/typeUtil';
import { getUser, listUserByOrganizationID } from 'src/graphql/queries';
import { useCurrentOrganizationsQuery } from '../organization';
import { useOrganization } from 'src/contexts/OrganizationContext';
import { useUser } from 'src/repository/useUser';
import { showUserFullName } from 'src/utils/showUserFullName';
import { addToGroup, removeUserFromGroup } from 'src/utils/apiTypeUtil';
import { useMemberManagerDeleteMutation } from '../memberManager';
import { logError } from 'src/utils/log';
import { useError } from 'src/contexts/ErrorContext';
import { awsApiAdapter } from 'src/utils/awsApiAdapter';
import * as mutations from 'coteam4/src/graphql/mutations';

// 組織ごとにユーザーをリスト（OrganizationContext使用）
export const useListUsersByOrganizationID = () => {
  const { users, isLoading, organization } = useOrganization();

  const filteredUsers = useMemo(() => {
    if (organization?.name === 'SELF-PRODUCE') {
      return users.filter((user) => user.deleteStatus !== DeleteStatus.deleted);
    }
    return users;
  }, [users, organization?.name]);

  return { users: filteredUsers, loading: isLoading };
};

// contextを使わない組織ごとにユーザーをリスト
export const useListMembersByOrganizationID = (organizationId: string) => {
  const { reportError } = useError();
  const [users, setUsers] = useState<User[]>([]);

  const {
    data,
    loading: userLoading,
    error: userError,
  } = useQuery<ListUserByOrganizationIDQuery, ListUserByOrganizationIDQueryVariables>(
    gql(listUserByOrganizationID),
    {
      variables: {
        organizationID: organizationId,
        limit: 1000,
      },
      skip: !organizationId,
      notifyOnNetworkStatusChange: true,
      // fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    if (userError) {
      console.error('ユーザー取得エラー', userError);
      reportError(userError);
    }

    const state = (data?.listUserByOrganizationID?.items.filter(nonNullable) ?? []) as User[];

    setUsers(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, userError]);

  return { users, loading: userLoading };
};

// 組織に属する招待済みユーザーをリスト（できたらGSI?）
export const useListActiveUsersByOrganizationID = () => {
  const {
    organization,
    loading: organizationLoading,
    error: organizationError,
  } = useCurrentOrganizationsQuery();

  const listUserRes = useQuery<
    ListUserByOrganizationIDQuery,
    ListUserByOrganizationIDQueryVariables
  >(gql(listUserByOrganizationID), {
    variables: {
      organizationID: organization?.id ?? '',
      limit: 1000,
    },
    skip: !organization,
    notifyOnNetworkStatusChange: true,
  });
  const users = (listUserRes.data?.listUserByOrganizationID?.items
    .filter(nonNullable)
    .filter((user) => user.status === UserStatus.ActivateComplete) ?? []) as User[];
  const loading = listUserRes.loading || organizationLoading;
  const error = listUserRes.error || organizationError;

  return { users, loading, error };
};

// 組織に属する招待済みかつ削除されていないユーザーをリスト（useUser repository使用）
export const useListActiveAndNotDeletedUsers = () => {
  const { activeUsers, isLoading } = useUser();
  return { users: activeUsers, loading: isLoading, error: null };
};

// 渡されたユーザーIDと合致するアイテムを取得してくる
export const useUserQuery = (userId?: string) => {
  const userRes = useQuery<GetUserQuery, GetUserQueryVariables>(gql(getUser), {
    variables: { id: userId ?? '' },
    // skip: !userId,
    // notifyOnNetworkStatusChange: true,
  });

  const user = userRes?.data?.getUser as User | null | undefined;

  return { user, ...userRes };
};

export const useUserManagersQuery = (userId?: string) => {
  const { user, ...userQuery } = useUserQuery(userId);

  const userManagers = user?.myManagers?.items.map((memberManager) => memberManager?.manager);

  return { userManagers, ...userQuery };
};
export const useUserMembersQuery = (userId?: string) => {
  const { user, ...userQuery } = useUserQuery(userId);

  const userMembers = user?.myMembers?.items.map((member) => member?.member);

  return { userMembers, ...userQuery };
};

// ----------------------------------------------------------------------

export const updateUserService = async (input: UpdateUserInput) => {
  const variables: UpdateUserMutationVariables = { input };
  return awsApiAdapter.graphqlMutation<UpdateUserMutation>({
    mutation: gql(mutations.updateUser as any) as any,
    variables,
  });
};

export const useUserUpdateMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(
    gql(updateUser),
    refetch
  );

  return userMutation;
};

export const useUserCreateMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation<CreateUserMutation, CreateUserMutationVariables>(
    gql(createUser),
    refetch
  );
  return userMutation;
};

export const useUserDeleteMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation<DeleteUserMutation, DeleteUserMutationVariables>(
    gql(deleteUser),
    refetch
  );
  return userMutation;
};

// ----------------------------------------------------------------------

export const useCognitoUserCreateMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation<CreateCognitoUserMutation, CreateCognitoUserMutationVariables>(
    gql(createCognitoUser),
    refetch
  );
  return userMutation;
};

export const useImportedUsersCreateMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation<
    CreateImportedUsersMutation,
    CreateImportedUsersMutationVariables
  >(gql(createImportedUsers), refetch);
  return userMutation;
};

export const useCognitoUserDeleteMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation(gql(deleteCognitoUser), refetch);
  return userMutation;
};

export const useDeleteUserTriggerMutation = (refetch?: { refetchQueries: any[] }) => {
  const deleteUserTriggerMutation = useMutation<
    DeleteUserTriggerMutation,
    DeleteUserTriggerMutationVariables
  >(gql(deleteUserTrigger), refetch);
  return deleteUserTriggerMutation;
};

// ----------------------------------------------------------------------

/**
 * 派生パターン
 */
export const useCurrentUserQuery = (refetch?: { refetchQueries: any[] }) => {
  const { currentUser, isLoading } = useUser();

  return { currentUser, loading: isLoading, error: null };
};

export const useCognitoResentEmailMutation = (refetch?: { refetchQueries: any[] }) => {
  const userMutation = useMutation<SendEmailMutation, SendEmailMutationVariables>(
    gql(sendEmail),
    refetch
  );
  return userMutation;
};

export const useUsersByTeamIDQuery = (teamID?: string) => {
  const { getDepartmentUsers, isLoading } = useUser();
  const departmentUsers = getDepartmentUsers(teamID ?? '');

  const teamUsers = departmentUsers.map((user) => ({
    fullName: showUserFullName(user),
    statusActions:
      user.status === UserStatus.Invited
        ? 'CancelInvitation'
        : user.admin
        ? 'removeAdmin'
        : 'addAdmin',
    managerItems: user.myManagers?.items.map((memberManager) => ({
      id: memberManager?.id,
      fullName: showUserFullName(memberManager?.manager),
      thumbnailPhotoUrl: memberManager?.manager.thumbnailPhotoUrl,
    })),
    ...user,
  }));

  return {
    teamUsers,
    loading: isLoading,
    error: null,
  };
};

export const useUserChangePermissionToAdminMutation = () => {
  const [updateUser, updateUserMutation] = useUserUpdateMutation();

  const changePermissionToAdmin = async (user: User) => {
    const tasks = [];
    tasks.push(updateUser({ variables: { input: { id: user.id, admin: true } } }));

    tasks.push(addToGroup(user.id, user.adminGroup));
    tasks.push(addToGroup(user.id, 'Admins'));
    await Promise.all(tasks);
  };

  const errors = updateUserMutation.error;
  const loading = updateUserMutation.loading;

  return {
    changePermissionToAdmin,
    errors,
    loading,
  };
};

export const useUserChangePermissionToMemberMutation = () => {
  const [updateUser, updateUserMutation] = useUserUpdateMutation();

  const changePermissionToMember = async (user: User) => {
    const tasks = [];
    tasks.push(updateUser({ variables: { input: { id: user.id, admin: false } } }));

    tasks.push(removeUserFromGroup(user.id, user.adminGroup));
    tasks.push(removeUserFromGroup(user.id, 'Admins'));
    await Promise.all(tasks);
  };

  const errors = updateUserMutation.error;
  const loading = updateUserMutation.loading;

  return {
    changePermissionToMember,
    errors,
    loading,
  };
};

export const useDeleteUserTrigger = () => {
  const [deleteUserTrigger, deleteUserTriggerMutation] = useDeleteUserTriggerMutation({
    refetchQueries: ['ListFullUsers', 'GetUser'],
  });
  const { reportError } = useError();
  const logicalDeleteUsers = async (userIDs: string[]) => {
    await deleteUserTrigger({ variables: { userIDs } });
    if (error) {
      reportError(error);
      return;
    }
  };

  const loading = deleteUserTriggerMutation.loading;
  const error = deleteUserTriggerMutation.error;

  return {
    logicalDeleteUsers,
    error,
    loading,
  };
};

export const useUserAndMemberManagerDeleteMutation = () => {
  const [deleteUser, deleteUserMutation] = useUserDeleteMutation({
    refetchQueries: ['ListFullUsers'],
  });
  const [deleteCognitoUser, deleteCognitoUserMutation] = useCognitoUserDeleteMutation();
  const [deleteMemberManager, deleteMemberManagerMutation] = useMemberManagerDeleteMutation({
    refetchQueries: ['ListMemberManagerByAdminGroup'],
  });

  const deleteUserAndMemberManager = async (user: User) => {
    const tasks: Promise<any>[] = [];
    const memberManagerIds: string[] = [];

    user?.myManagers?.items.forEach((memberManager) => {
      if (memberManager) {
        memberManagerIds.push(memberManager?.id);
      }
    });
    user?.myMembers?.items.forEach((memberManager) => {
      if (memberManager) {
        memberManagerIds.push(memberManager?.id);
      }
    });

    memberManagerIds.forEach((memberManagerId) => {
      tasks.push(deleteMemberManager({ variables: { input: { id: memberManagerId } } }));
    });

    await Promise.all(tasks);

    try {
      await deleteCognitoUser({ variables: { username: user.id } });
    } catch (error) {
      //cognitoのデータが存在していない場合はエラー握り潰す
      //Sentryには通知する
      logError(`failed to delete userID: ${user.id} ${error}`);
    }

    await deleteUser({ variables: { input: { id: user.id } } });
  };

  const errors =
    deleteUserMutation.error ||
    deleteCognitoUserMutation.error ||
    deleteMemberManagerMutation.error;
  const loading =
    deleteUserMutation.loading ||
    deleteCognitoUserMutation.loading ||
    deleteMemberManagerMutation.loading;

  return {
    deleteUserAndMemberManager,
    errors,
    loading,
  };
};
